import { useRecoilValue } from 'recoil';
import { GetSessionError } from '~/backend/connector-api/connector-components';
import { SessionPresentation } from '~/backend/connector-api/connector-schemas';
import {
    APIType,
    TranslationCustomizationFile,
    UserCustomization,
} from '~/backend/types';
import { WorkflowTargetIds } from '~/hooks/workflows/useWorkflowTargetIds';
import {
    useBackendModels,
    useModelCustomizations,
} from '~/hooks/workflows/backend/useBackend';
import useImageTranslationTask from '~/hooks/workflows/connector/tasks/useImageTranslationTask';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import ImageTranslation from './ImageTranslation';

type ImageTranslationConnectorWorkflowProps = {
    sessionData: SessionPresentation | undefined;
    environmentObjectIds: WorkflowTargetIds | undefined;
    isLoadingSession: boolean;
    error: Error | GetSessionError | null;
};

export default function ImageTranslationConnectorWorkflow({
    sessionData,
    environmentObjectIds,
    isLoadingSession,
    error,
}: ImageTranslationConnectorWorkflowProps) {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const { start: startProcessing, isSubmitting } = useImageTranslationTask({
        patientID: sessionData?.payload?.patient.id || selectedPatientId || '',
        studyId:
            environmentObjectIds?.primaryImage?.studyInstanceUID ??
            (sessionData?.payload?.context_image.study_instance_uid || ''),
        seriesId:
            environmentObjectIds?.primaryImage?.seriesInstanceUID ??
            (sessionData?.payload?.context_image.series_instance_uid || ''),
    });
    const { data: availableModels = [], isLoading: isLoadingModels } =
        useBackendModels(APIType.TRANSLATION);

    const {
        data: modelCustomizations = [],
        isLoading: isLoadingCustomizations,
    } = useModelCustomizations(APIType.TRANSLATION, { refetchInterval: 10000 });

    return (
        <ImageTranslation
            key={'image-translation-modal'}
            availableModels={availableModels}
            modelCustomizations={
                modelCustomizations as UserCustomization<TranslationCustomizationFile>[]
            }
            onSubmit={startProcessing}
            isLoadingModels={isLoadingModels || isLoadingCustomizations}
            isLoadingSession={isLoadingSession}
            isSubmitting={isSubmitting}
            error={error}
            showPseudonymization={false}
        />
    );
}
